import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLazyQuery, useQuery } from '@apollo/client/react';
import { CombinedGraphQLErrors } from '@apollo/client/errors';
import classNames from 'classnames';
import { ADD_BROADCAST_LIVE, AddBroadcastLiveData, AddBroadcastLiveVariables } from '@jwp/ott-common/src/graphql/mutation/broadcastLive.mutation';
import { GET_S3_UPLOAD_PRESIGNED_URL, GetS3UploadPresignedUrlData } from '@jwp/ott-common/src/graphql/query/reels.queries';
import { GET_DROPDOWNS, GetDropdownsData, GetDropdownsVariables, DropdownType } from '@jwp/ott-common/src/graphql/query/broadcastLive.queries';
import { getAuthApolloClient, getApolloClient } from '@jwp/ott-common/src/graphql/ApolloClient';
import { generateS3Key } from '@jwp/ott-common/src/utils/s3';
import {
  BROADCAST_LIVE_EVENT_MAX_DURATION_MS,
  getMaxBroadcastEventEndTime,
  validateBroadcastEventSchedule,
  validateBroadcastEventTimeSchedule,
  validateBroadcastEventDateSchedule
} from '@jwp/ott-common/src/utils/broadcastLive';
import { useAccountStore } from '@jwp/ott-common/src/stores/AccountStore';
import { PATH_BROADCAST_LIVE_DETAILS } from '@jwp/ott-common/src/paths';
import CalendarIcon from '@jwp/ott-theme/assets/icons/Calendar.svg?react';
import ClockIcon from '@jwp/ott-theme/assets/icons/ClockIcon.svg?react';
import InfoIcon from '@jwp/ott-theme/assets/icons/info.svg?react';
import HelpIcon from '@jwp/ott-theme/assets/icons/Help.svg?react';

import TextField from '../TextField/TextField';
import Dropdown from '../Dropdown/Dropdown';
import Button from '../Button/Button';
import Spinner from '../Spinner/Spinner';
import Icon from '../Icon/Icon';
import Tooltip from '../Tooltip/Tooltip';
import Calendar from '../Calendar/Calendar';
import UploadBox from '../UploadBox/UploadBox';

import PickerField from './PickerField';
import styles from './CreateLiveStreamModal.module.scss';

const THUMBNAIL_SIZE_LIMIT_MB = 1;

type DropdownOption = { value: string; label: string };

const useDropdownOptions = (ddType: DropdownType): DropdownOption[] => {
  const { data } = useQuery<GetDropdownsData, GetDropdownsVariables>(GET_DROPDOWNS, {
    client: getAuthApolloClient(),
    variables: { ddType },
    errorPolicy: 'all',
  });
  return useMemo(() => (data?.getDropdowns?.dropdowns || []).map((item) => ({ value: item.ddValue, label: item.ddLabel })), [data]);
};

const generateTimeOptions = (): string[] => {
  const options: string[] = [];
  for (let minutes = 0; minutes < 24 * 60; minutes += 15) {
    const h24 = Math.floor(minutes / 60);
    const m = minutes % 60;
    const period = h24 < 12 ? 'AM' : 'PM';
    const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
    options.push(`${String(h12).padStart(2, '0')}:${String(m).padStart(2, '0')} ${period}`);
  }
  return options;
};

const TIME_OPTIONS = generateTimeOptions();

// Normalize a free-text time entry to hh:mm AM/PM format.
// Returns null if the text cannot be parsed.
const normalizeTimeInput = (raw: string): string | null => {
  const trimmed = raw.trim().toUpperCase();

  // Accept:
  // 10:30 AM
  // 10:30AM
  // 1030 AM
  // 10 AM
  // 10AM
  const match = /^(\d{1,2})(?::?(\d{1,2}))?\s*(AM|PM)?$/.exec(trimmed);

  if (!match) { return null; }

  let hours = Number.parseInt(match[1], 10);
  const minutes = Number.parseInt(match[2] || '0', 10);
  let period = match[3];

  // hard validation
  if (Number.isNaN(hours) || Number.isNaN(minutes)) { return null; }

  // Reject hour >23
  if (hours > 23) { return null; }

  // Reject invalid minutes
  if (minutes < 0 || minutes > 59) { return null; }

  // If AM/PM given validate 12-hour format
  if (period && (hours < 1 || hours > 12)) {
    return null;
  }

  // Infer period if absent
  if (!period) {
    period = hours < 12 ? 'AM' : 'PM';
  }

  // Convert to 24h
  if (match[3]) {
    if (period === 'PM' && hours !== 12) {
      hours += 12;
    }

    if (period === 'AM' && hours === 12) {
      hours = 0;
    }
  }

  const h12 = hours % 12 === 0 ? 12 : hours % 12;

  return `${String(h12).padStart(2, '0')}:${String(minutes).padStart(2, '0')} ${hours < 12 ? 'AM' : 'PM'}`;
};

const formatTimeLabel = (date: Date): string => {
  const h24 = date.getHours();
  const m = date.getMinutes();
  const period = h24 < 12 ? 'AM' : 'PM';
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  return `${String(h12).padStart(2, '0')}:${String(m).padStart(2, '0')} ${period}`;
};

const formatDateDisplay = (date: Date | null): string => {
  if (!date) { return ''; }
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${mm}/${dd}/${date.getFullYear()}`;
};

const parseTimeLabel = (label: string): { hours: number; minutes: number } | null => {
  const match = /^(\d{1,2}):(\d{2})\s(AM|PM)$/.exec(label);
  if (!match) { return null; }
  let hours = Number.parseInt(match[1], 10);
  const minutes = Number.parseInt(match[2], 10);
  const period = match[3];
  if (period === 'PM' && hours !== 12) { hours += 12; }
  if (period === 'AM' && hours === 12) { hours = 0; }
  return { hours, minutes };
};

const combineDateTime = (date: Date | null, timeLabel: string): Date | null => {
  if (!date || !timeLabel) { return null; }
  const time = parseTimeLabel(timeLabel);
  if (!time) { return null; }
  return new Date(date.getFullYear(), date.getMonth(), date.getDate(), time.hours, time.minutes, 0, 0);
};

const toIsoUtc = (date: Date | null): string | null => {
  if (!date) { return null; }
  return `${date.toISOString().split('.')[0]}Z`;
};

const formatUtcDisplay = (date: Date | null): string => {
  if (!date) { return ''; }
  const iso = date.toISOString();
  const [datePart, timePart] = iso.split('T');
  return `${datePart}, ${timePart.split('.')[0]}`;
};

const TimeList: React.FC<{ value: string; onSelect: (value: string) => void }> = ({ value, onSelect }) => (
  <ul className={styles.timeList} role="listbox">
    {TIME_OPTIONS.map((option) => (
      <li
        key={option}
        role="option"
        aria-selected={option === value}
        tabIndex={0}
        className={classNames(styles.timeOption, { [styles.timeOptionSelected]: option === value })}
        onClick={() => onSelect(option)}
        onKeyDown={(e) => e.key === 'Enter' && onSelect(option)}
      >
        {option}
      </li>
    ))}
  </ul>
);

const CreateLiveStreamModal: React.FC = () => {
  const { t } = useTranslation(['video', 'upload', 'common']);
  const navigate = useNavigate();
  const user = useAccountStore((state) => state.user);
  const [streamType, setStreamType] = useState<'event' | '247'>('event');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [thumbnailFile, setThumbnailFile] = useState<File | null>(null);
  const [thumbnailPreview, setThumbnailPreview] = useState<string | null>(null);
  const [thumbnailError, setThumbnailError] = useState<string | null>(null);
  const [ingestRegion, setIngestRegion] = useState('');
  const [ingestFormat, setIngestFormat] = useState('');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [startTime, setStartTime] = useState('');
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [endTime, setEndTime] = useState('');
  const [contentType, setContentType] = useState('');
  const [dvrWindow, setDvrWindow] = useState('');

  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [startTimeError, setStartTimeError] = useState('');
  const [endTimeError, setEndTimeError] = useState('');

  const [getPresignedUrl] = useLazyQuery<GetS3UploadPresignedUrlData, { key: string }>(GET_S3_UPLOAD_PRESIGNED_URL);

  const regionOptions = useDropdownOptions('ingest_region');
  const formatOptions = useDropdownOptions('ingest_format');
  const contentTypeOptions = useDropdownOptions('content_type');
  const dvrOptions = useDropdownOptions('dvr_window');

  // Default schedule: today, start = now + 15 min, end = start + 10 min (user can change)
  useEffect(() => {
    const start = new Date(Date.now() + 15 * 60 * 1000);
    const end = new Date(start.getTime() + 10 * 60 * 1000);
    setStartDate(start);
    setStartTime(formatTimeLabel(start));
    setEndDate(end);
    setEndTime(formatTimeLabel(end));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Preselect the first available option for the non-required dropdowns once loaded
  useEffect(() => {
    if (!contentType && contentTypeOptions.length > 0) {
      setContentType(contentTypeOptions[0].value);
    }
  }, [contentType, contentTypeOptions]);

  useEffect(() => {
    if (!dvrWindow && dvrOptions.length > 0) {
      setDvrWindow(dvrOptions[0].value);
    }
  }, [dvrWindow, dvrOptions]);

  const startDateTime = useMemo(() => combineDateTime(startDate, startTime), [startDate, startTime]);
  const endDateTime = useMemo(() => combineDateTime(endDate, endTime), [endDate, endTime]);
  const scheduleError = useMemo(
    () => (startDateTime && endDateTime ? validateBroadcastEventSchedule(startDateTime, endDateTime) : null),
    [startDateTime, endDateTime],
  );

  const scheduleDateError = useMemo(
    () =>
      startDateTime && endDateTime
        ? validateBroadcastEventDateSchedule(startDateTime, endDateTime)
        : null,
    [startDateTime, endDateTime],
  );

  const scheduleTimeError = useMemo(
    () =>
      startDateTime && endDateTime
        ? validateBroadcastEventTimeSchedule(startDateTime, endDateTime)
        : null,
    [startDateTime, endDateTime],
  );


  const maxEndDateTime = useMemo(
    () => (startDateTime ? getMaxBroadcastEventEndTime(startDateTime) : null),
    [startDateTime],
  );


  const getScheduleErrorMessage = (error: ReturnType<typeof validateBroadcastEventSchedule>, time?: string): string => {
    switch (error) {
      case 'end_before_start':
        return t('video:broadcast_live.end_after_start');
      case 'min_duration':
        return t('video:broadcast_live.min_duration');
      case 'max_duration':
        return t('video:broadcast_live.max_duration', time == 'time' ? {
          value: startTime,
          defaultValue: `Date may not be after ${startTime}`
        } : {
          value: formatDateDisplay(endDate),
          defaultValue: `Date may not be after ${formatDateDisplay(endDate)}`
        });
      case 'start_too_soon':
        return t('video:broadcast_live.start_too_soon');
      default:
        return '';
    }
  };

  // Keep end within the 24h JW event limit when start changes.
  // useEffect(() => {
  //   if (!startDateTime || !endDateTime) return;

  //   let nextEnd: Date | null = null;
  //   if (endDateTime.getTime() <= startDateTime.getTime()) {
  //     nextEnd = new Date(startDateTime.getTime() + 10 * 60 * 1000);
  //   } else if (endDateTime.getTime() > startDateTime.getTime() + BROADCAST_LIVE_EVENT_MAX_DURATION_MS) {
  //     nextEnd = getMaxBroadcastEventEndTime(startDateTime);
  //   }

  //   if (!nextEnd || nextEnd.getTime() === endDateTime.getTime()) return;

  //   setEndDate(nextEnd);
  //   setEndTime(formatTimeLabel(nextEnd));
  // }, [startDateTime, endDateTime]);

  const handleThumbnailSelect = (file: File | null) => {
    setThumbnailError(null);
    if (!file) { return; }
    if (!file.type.startsWith('image/')) {
      setThumbnailError(t('upload:thumbnail_picker.errors.invalid_file_type'));
      return;
    }
    if (file.size > THUMBNAIL_SIZE_LIMIT_MB * 1024 * 1024) {
      setThumbnailError(t('upload:thumbnail_picker.errors.file_size', { size: THUMBNAIL_SIZE_LIMIT_MB }));
      return;
    }
    setThumbnailFile(file);
    setThumbnailPreview(URL.createObjectURL(file));
  };

  const handleThumbnailDelete = () => {
    if (thumbnailPreview) { URL.revokeObjectURL(thumbnailPreview); }
    setThumbnailFile(null);
    setThumbnailPreview(null);
    setThumbnailError(null);
  };

  const handleTimeChange = (
    value: string,
    setTime: React.Dispatch<React.SetStateAction<string>>,
    setError: React.Dispatch<React.SetStateAction<string>>,
  ) => {
    setError(''); // clear previous state first

    if (!value.trim()) {
      setTime('');
      return;
    }

    const normalized = normalizeTimeInput(value);

    if (normalized) {
      setTime(normalized);
      return;
    }

    // Keep typed text visible
    setTime(value);

    setError(t('video:broadcast_live.valid_time'));
  };
  const isValid =
    !!title.trim() &&
    !!thumbnailFile &&
    !!ingestRegion &&
    !!ingestFormat &&
    !!startDateTime &&
    !!endDateTime &&
    !startTimeError &&
    !endTimeError &&
    !scheduleError;

  const uploadThumbnail = async (file: File): Promise<string> => {
    const key = generateS3Key(user?.id || 'anonymous', file.name, 'broadcast-thumbnails');
    const { data } = await getPresignedUrl({ variables: { key } });
    const url = data?.getS3UploadPresignedUrl?.preSignedUrl;
    if (!url) { throw new Error('Failed to get upload URL'); }
    const response = await fetch(url, {
      method: 'PUT',
      body: file,
      headers: { 'Content-Type': file.type || 'image/jpeg' },
    });
    if (!response.ok) { throw new Error('Failed to upload thumbnail'); }
    return key;
  };

  const handleCreate = async () => {
    setSubmitted(true);
    setFormError(null);
    if (!isValid || !thumbnailFile || !startDateTime || !endDateTime) { return; }

    try {
      setSubmitting(true);
      const thumbnailKey = await uploadThumbnail(thumbnailFile);

      const input = {
        title: title.trim(),
        ...(description.trim() ? { description: description.trim() } : {}),
        thumbnailUrl: thumbnailKey,
        clusterRegion: ingestRegion,
        ingestFormat,
        streamType,
        dvrWindow: Number.parseInt(dvrWindow, 10) || 0,
        startTime: toIsoUtc(startDateTime) as string,
        endTime: toIsoUtc(endDateTime) as string,
      };

      let blId: string | undefined;
      let lastError: string | undefined;

      for (const client of [getAuthApolloClient(), getApolloClient()]) {
        const result = await client.mutate<AddBroadcastLiveData, AddBroadcastLiveVariables>({
          mutation: ADD_BROADCAST_LIVE,
          variables: { input },
          errorPolicy: 'all',
        });

        blId = result.data?.addBroadcastLive?.broadcastLive?.blId;
        if (blId) { break; }

        lastError =
          result.errors?.[0]?.message ||
          (result.errors?.[0]?.extensions?.response as { message?: string } | undefined)?.message ||
          result.data?.addBroadcastLive?.message;
      }

      if (blId) {
        navigate(`${PATH_BROADCAST_LIVE_DETAILS}/${blId}`, { replace: true });
        return;
      }

      throw new Error(lastError || 'Failed to create live stream');
    } catch (error) {
      let message = t('video:broadcast_live.create_error');
      if (error instanceof Error) {
        message = error.message;
      } else if (CombinedGraphQLErrors.is(error)) {
        message = error.errors[0]?.message || message;
      }
      setFormError(message);
      setSubmitting(false);
    }
  };

  const showError = (condition: boolean) => submitted && condition;

  return (
    <div className={styles.container}>
      <h2 className={styles.title}>{t('video:broadcast_live.create_title')}</h2>

      {formError && <div className={styles.formError}>{formError}</div>}

      <div className={styles.scrollArea} data-scroll-container>
        <div className={styles.streamTypeRow}>
          <div className={styles.fieldLabel}>
            {t('video:broadcast_live.stream_type')}
            <span className={styles.required}>*</span>
          </div>
          <div className={styles.radioGroup}>
            <label className={classNames(styles.radio, { [styles.radioActive]: streamType === 'event' })}>
              <input type="radio" name="streamType" checked={streamType === 'event'} onChange={() => setStreamType('event')} />
              <span className={styles.radioMark} />
              {t('video:broadcast_live.live_event')}
            </label>
            <label className={classNames(styles.radio, styles.radioDisabled)}>
              <input type="radio" name="streamType" disabled checked={false} readOnly />
              <span className={styles.radioMark} />
              {t('video:broadcast_live.always_on')}
            </label>
          </div>
        </div>

        <div className={styles.divider} />

        <div className={styles.topGrid}>
          <div className={styles.leftColumn}>
            <TextField
              name="title"
              label={t('video:broadcast_live.title_label')}
              value={title}
              onChange={(e) => setTitle((e.target as HTMLInputElement).value)}
              placeholder={t('video:broadcast_live.title_placeholder')}
              required
              maxLength={100}
              error={showError(!title.trim())}
              helperText={showError(!title.trim()) ? t('upload:this_field_is_required') : ''}
              showOptionalLabel={false}
            />
            <TextField
              name="description"
              label={t('video:broadcast_live.description_label')}
              value={description}
              onChange={(e) => setDescription((e.target as HTMLTextAreaElement).value)}
              placeholder={t('video:broadcast_live.description_placeholder')}
              multiline
              maxLength={500}
              showOptionalLabel={false}
            />
          </div>

          <div className={styles.rightColumn}>
            <div className={styles.thumbnailLabel}>
              {t('video:broadcast_live.thumbnail_label')}
              <span className={styles.thumbnailHint}>{t('video:broadcast_live.thumbnail_size')}</span>
              <span className={styles.required}>*</span>
            </div>
            <UploadBox
              type="image"
              accept="image/*"
              height={210}
              poster={thumbnailPreview || undefined}
              previewUrl={thumbnailPreview}
              selectedFile={thumbnailFile}
              onFileSelect={handleThumbnailSelect}
              onDelete={thumbnailPreview ? handleThumbnailDelete : undefined}
              title={t('video:broadcast_live.add_image')}
              hint={t('video:broadcast_live.thumbnail_max_size', { size: THUMBNAIL_SIZE_LIMIT_MB })}
              showControlsOnHover
              error={showError(!thumbnailFile) || !!thumbnailError}
              className={styles.thumbnailBox}
            />
            {thumbnailError && <div className={styles.fieldError}>{thumbnailError}</div>}
            {showError(!thumbnailFile) && !thumbnailError && <div className={styles.fieldError}>{t('upload:this_field_is_required')}</div>}
          </div>
        </div>

        <div className={styles.twoCol}>
          <Dropdown
            name="ingestRegion"
            label={t('video:broadcast_live.ingest_region')}
            value={ingestRegion}
            onChange={(e) => setIngestRegion(e.target.value)}
            options={regionOptions}
            defaultLabel={t('video:broadcast_live.select_region')}
            required
            fullWidth
            error={showError(!ingestRegion)}
            helperText={showError(!ingestRegion) ? t('upload:this_field_is_required') : ''}
          />
          <Dropdown
            name="ingestFormat"
            label={
              <span className={styles.labelWithTooltip}>
                {t('video:broadcast_live.ingest_format')}
                <Tooltip title={t('video:broadcast_live.ingest_format_tooltip')}>
                  <Icon icon={HelpIcon} className={styles.tooltipIcon} />
                </Tooltip>
              </span>
            }
            value={ingestFormat}
            onChange={(e) => setIngestFormat(e.target.value)}
            options={formatOptions}
            defaultLabel={t('video:broadcast_live.select_format')}
            required
            fullWidth
            error={showError(!ingestFormat)}
            helperText={showError(!ingestFormat) ? t('upload:this_field_is_required') : ''}
          />
        </div>

        <div className={styles.threeCol}>
          <PickerField
            label={t('video:broadcast_live.start_date')}
            required
            icon={CalendarIcon}
            popoverWidth={300}
            value={formatDateDisplay(startDate)}
            placeholder="MM/DD/YYYY"
            error={showError(!startDate) || showError(scheduleError === 'start_too_soon')}
            helperText={
              showError(scheduleError === 'start_too_soon')
                ? getScheduleErrorMessage('start_too_soon')
                : showError(!startDate)
                  ? t('upload:this_field_is_required')
                  : ''
            }
          >
            {(close) => (
              <Calendar
                value={startDate}
                minDate={new Date()}
                onSelect={(date) => {
                  setStartDate(date);
                  setEndDate(date);
                  close();
                }}
              />
            )}
          </PickerField>

          <PickerField
            label={t('video:broadcast_live.local_time')}
            required
            icon={ClockIcon}
            popoverWidth={200}
            value={startTime}
            placeholder="12:00 AM"
            editable
            onValueChange={(val) =>
              handleTimeChange(
                val,
                setStartTime,
                setStartTimeError
              )
            }
            error={showError(!startTime) || !!startTimeError}

            helperText={
              startTimeError ||
              (showError(!startTime)
                ? t('upload:this_field_is_required')
                : '')
            }
          >
            {(close) => (
              <TimeList
                value={startTime}
                onSelect={(value) => {
                  setStartTime(value);
                  setStartTimeError(''); // clear previous invalid state

                  close();
                }}
              />
            )}
          </PickerField>

          <div className={styles.utcField}>
            <div className={styles.fieldLabel}>{t('video:broadcast_live.utc')}</div>
            <div className={styles.utcValue}>{formatUtcDisplay(startDateTime) || '--'}</div>
          </div>
        </div>

        <div className={styles.threeCol}>
          <PickerField
            label={t('video:broadcast_live.end_date')}
            required
            icon={CalendarIcon}
            popoverWidth={300}
            value={formatDateDisplay(endDate)}
            placeholder="MM/DD/YYYY"
            error={
              showError(!endDate) || scheduleDateError === 'max_duration' ||
              showError(scheduleDateError === 'end_before_start') ||
              showError(scheduleDateError === 'min_duration')
            }
            helperText={
              scheduleDateError && (submitted || scheduleDateError === 'max_duration' || scheduleDateError === 'end_before_start' || scheduleDateError === 'min_duration')
                ? getScheduleErrorMessage(scheduleDateError)
                : showError(!endDate)
                  ? t('upload:this_field_is_required')
                  : ''
            }
          >
            {(close) => (
              <Calendar
                value={endDate}
                minDate={startDate || new Date()}
                onSelect={(date) => {
                  setEndDate(date);
                  close();
                }}
              />
            )}
          </PickerField>

          <PickerField
            label={t('video:broadcast_live.local_time')}
            required
            icon={ClockIcon}
            popoverWidth={200}
            value={endTime}
            placeholder="12:00 AM"
            editable
            onValueChange={(val) =>
              handleTimeChange(
                val,
                setEndTime,
                setEndTimeError
              )
            }
            error={
              showError(!endTime) ||
              !!endTimeError ||
              (!!scheduleTimeError && (submitted || scheduleTimeError === 'max_duration'))  // ✅ Any scheduleError
            }
            helperText={
              endTimeError ||
              (scheduleTimeError && (submitted || scheduleTimeError === 'max_duration')
                ? getScheduleErrorMessage(scheduleTimeError, 'time')
                : showError(!endTime)
                  ? t('upload:this_field_is_required')
                  : ''
              )
            }
          >
            {(close) => (
              <TimeList
                value={endTime}
                onSelect={(value) => {
                  setEndTime(value);
                  setEndTimeError('');
                  close();
                }}
              />
            )}
          </PickerField>

          <div className={styles.utcField}>
            <div className={styles.fieldLabel}>{t('video:broadcast_live.utc')}</div>
            <div className={styles.utcValue}>{formatUtcDisplay(endDateTime) || '--'}</div>
            {maxEndDateTime && (
              <div className={styles.utcHint}>
                {t('video:broadcast_live.max_end_utc', { value: formatUtcDisplay(maxEndDateTime) })}
              </div>
            )}
          </div>
        </div>

        <div className={styles.twoCol}>
          <Dropdown
            name="contentType"
            label={
              <span className={styles.labelWithTooltip}>
                {t('video:broadcast_live.content_type')}
                <Tooltip title={t('video:broadcast_live.content_type_tooltip')}>
                  <Icon icon={HelpIcon} className={styles.tooltipIcon} />
                </Tooltip>
              </span>
            }
            value={contentType}
            onChange={(e) => setContentType(e.target.value)}
            options={contentTypeOptions}
            hideOptional
            fullWidth
          />
          <Dropdown
            name="dvrWindow"
            label={
              <span className={styles.labelWithTooltip}>
                {t('video:broadcast_live.dvr_window')}
                <Tooltip title={t('video:broadcast_live.dvr_window_tooltip')}>
                  <Icon icon={HelpIcon} className={styles.tooltipIcon} />
                </Tooltip>
              </span>
            }
            value={dvrWindow}
            onChange={(e) => setDvrWindow(e.target.value)}
            options={dvrOptions}
            hideOptional
            fullWidth
          />
        </div>
      </div>

      <div className={styles.footer}>
        <div className={styles.footerNote}>
          <Icon icon={InfoIcon} className={styles.footerIcon} />
          {t('video:broadcast_live.processing_note')}
        </div>
        <Button
          label={t('video:broadcast_live.create_submit_button')}
          variant="contained"
          color="primary"
          onClick={handleCreate}
          disabled={submitting || !isValid}
          className={styles.createButton}
        />
      </div>

      {submitting && (
        <div className={styles.loadingOverlay}>
          <Spinner size="medium" />
        </div>
      )}
    </div>
  );
};

export default CreateLiveStreamModal;
